import type { FC } from "react";
import { Route, Routes } from "react-router";
import { PageIndex } from "@/features/common/pages/PageIndex";
import { PageLogin } from "@/features/auth/pages/PageLogin";
import { FrameAuth } from "@/features/auth/FrameAuth";
import { PageRegister } from "@/features/auth/pages/PageRegister";
import { PageEmailVerify } from "@/features/auth/pages/PageEmailVerify";
import { PageInitiateForgotPassword } from "@/features/auth/pages/PageInitiateForgotPassword";
import { PageConcludeForgotPassword } from "@/features/auth/pages/PageConcludeForgotPassword";
import { FrameDashboard } from "@/features/common/FrameDashboard";
import { PagePerformanceDeltas } from "@/features/performanceDeltas/pages/PagePerformanceDeltas";
import { PageEvItems } from "@/features/ev/pages/PageEvItems";
import { PageNotifications } from "@/features/notifications/pages/PageNotifications";
import { PageProduct } from "@/features/products/pages/PageProduct";
import { PageDashboard } from "@/features/dashboard/pages/PageDashboard";
import { PageCollections } from "@/features/collections/pages/PageCollections";
import { PagePublicCollection } from "@/features/collections/pages/PagePublicCollection";
import { PageClonePublicCollectionRedirect } from "@/features/collections/pages/PageClonePublicCollectionRedirect";
import { PagePriceOverrides } from "@/features/priceOverrides/pages/PagePriceOverrides";
import { PageChangePassword } from "@/features/settings/pages/PageChangePassword";
import { PagePaymentCards } from "@/features/paymentCards/pages/PagePaymentCards";
import { PageBrowse } from "@/features/products/pages/PageBrowse";
import { PageMfa } from "@/features/settings/pages/PageMfa";
import { PageMfaEnabled } from "@/features/auth/pages/PageMfaEnabled";
import { PageMfaDisabled } from "@/features/auth/pages/PageMfaDisabled";
import { PageCsvTutorial } from "@/features/import/pages/PageCsvTutorial";
import { PageCollection } from "@/features/collections/pages/PageCollection";
import { TabCollectionSummary } from "@/features/collections/pages/TabCollectionSummary";
import { PageCollectionExport } from "@/features/collections/pages/PageCollectionExport";
import { TabCollectionTools } from "@/features/collections/pages/TabCollectionTools";
import { TabCollectionEdit } from "@/features/collections/pages/TabCollectionEdit";
import { TabCollectionPerformanceDeltas } from "@/features/collections/pages/TabCollectionPerformanceDeltas";
import { TabCollectionSettings } from "@/features/collections/pages/TabCollectionSettings";
import { PageEmblems } from "@/features/emblems/pages/PageEmblems";
import { BulkTransactionsStateProvider } from "@/lib/state/BulkTransactionState";
import { PageMassTransactionEntry } from "@/features/collections/pages/PageMassTransactionEntry";
import { PageImport } from "@/features/import/pages/PageImport";
import { PagePriceAlerts } from "@/features/priceAlerts/pages/PagePriceAlerts";
import { PageSubscription } from "@/features/subscriptions/pages/PageSubscription";

export const Router: FC = () => {
	return (
		<Routes>
			<Route index element={<PageIndex />} />
			<Route path="public/:shareToken" element={<PagePublicCollection />} />
			<Route path="public/:shareToken/clone-public-collection" element={<FrameDashboard />}>
				<Route index element={<PageClonePublicCollectionRedirect />} />
			</Route>
			<Route path="login" element={<FrameAuth />}>
				<Route index element={<PageLogin />} />
				<Route path="register" element={<PageRegister />} />
				<Route path="email-verify" element={<PageEmailVerify />} />
				<Route path="forgot-password">
					<Route path="initiate" element={<PageInitiateForgotPassword />} />
					<Route path="conclude" element={<PageConcludeForgotPassword />} />
				</Route>
				<Route path="mfa">
					<Route path="enabled" element={<PageMfaEnabled />} />
					<Route path="disabled" element={<PageMfaDisabled />} />
				</Route>
			</Route>

			<Route path="dashboard" element={<FrameDashboard />}>
				<Route index element={<PageDashboard />} />
			</Route>

			<Route path="collections" element={<FrameDashboard />}>
				<Route index element={<PageCollections />} />
				<Route path="csv-import" element={<PageImport />} />
				<Route path="csv-tutorial" element={<PageCsvTutorial />} />
				<Route path=":collectionId" element={<PageCollection />}>
					<Route index element={<TabCollectionSummary />} />
					<Route path="csv-export" element={<PageCollectionExport />} />

					<Route path="transactions">
						<Route path="csv-import" element={<PageImport />} />
						<Route path="bulk" element={<BulkTransactionsStateProvider><PageMassTransactionEntry /></BulkTransactionsStateProvider>} />
						<Route path=":variantId" element={<TabCollectionEdit />} />
					</Route>

					<Route path="trends" element={<TabCollectionPerformanceDeltas />} />
					<Route path="edit" element={<TabCollectionEdit />} />
					<Route path="tools" element={<TabCollectionTools />} />
					<Route path="settings" element={<TabCollectionSettings />} />
				</Route>
			</Route>

			<Route path="browse" element={<FrameDashboard />}>
				<Route index element={<PageBrowse />} />
				<Route path="product">
					<Route path=":productId" element={<PageProduct />} />
				</Route>
			</Route>

			<Route path="market-trends" element={<FrameDashboard />}>
				<Route index element={<PagePerformanceDeltas />} />
			</Route>

			<Route path="ev-tracker" element={<FrameDashboard />}>
				<Route index element={<PageEvItems />} />
			</Route>

			<Route path="notifications" element={<FrameDashboard />}>
				<Route index element={<PageNotifications />} />
			</Route>

			<Route path="price-overrides" element={<FrameDashboard />}>
				<Route index element={<PagePriceOverrides />} />
			</Route>

			<Route path="price-alerts" element={<FrameDashboard />}>
				<Route index element={<PagePriceAlerts />} />
			</Route>

			<Route path="emblems" element={<FrameDashboard />}>
				<Route index element={<PageEmblems />} />
			</Route>

			<Route path="settings" element={<FrameDashboard />}>
				<Route path="change-password" element={<PageChangePassword />} />
				<Route path="mfa" element={<PageMfa />} />
				<Route path="payment-cards" element={<PagePaymentCards />} />
				<Route path="subscription" element={<PageSubscription />} />
			</Route>
		</Routes>
	);
};
